import { Button, Input, Spin, Popconfirm } from 'antd';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { ReactSVG } from 'react-svg';
import dayjs from 'dayjs';
import JobOrdersFilter from '../Elements/Filters/JobOrdersFilter';
import { PAGE_SIZE, PAGE_SIZE_OPTIONS } from '../../constants';
import JobOrdersTable from '../Elements/Tables/JobOrdersTable';
import { useDispatch, useSelector } from 'react-redux';
import { doGetJobOrders, doUnassignJobOrder } from '../../modules/jobOrders/stores/thunk';
import { selectAllJobOrders } from '../../modules/jobOrders/stores/slice';
import queryString from 'query-string';
import * as jobOrdersApi from '../../modules/jobOrders/api';

const JobOrdersPage = () => {
  const dispatch = useDispatch();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [searchParams] = useSearchParams();
  let location = useLocation();
  let navigate = useNavigate();
  const isFetching = useSelector((state) => state.jobOrder.isFetching);
  const metadata = useSelector((state) => state.jobOrder.metadata);
  const jobOrders = useSelector((state) => selectAllJobOrders(state));

  useEffect(() => {
    setSearchValue(query.q || '');
  }, []);

  const query = Object.fromEntries(searchParams);
  const hasActiveQueryParams = Object.keys(query).length > 0;
  const currentPage = query.current || 1;
  const itemsPerPage = query.pageSize || PAGE_SIZE;
  const pagination = {
    current: currentPage || 1,
    pageSize: itemsPerPage,
    locale: { items_per_page: 'Job Orders/Page' },
    total: metadata.totalItems || jobOrders.length,
    pageSizeOptions: PAGE_SIZE_OPTIONS,
    showSizeChanger: true,
    showTotal: (total) => `${total} Job Orders`,
  };

  useEffect(() => {
    const deliveryDateFrom = query.deliveryDateFrom ? dayjs(query.deliveryDateFrom).startOf('day').toISOString() : null;
    const deliveryDateTo = query.deliveryDateTo ? dayjs(query.deliveryDateTo).endOf('day').toISOString() : null;
    dispatch(doGetJobOrders({ page: currentPage, take: itemsPerPage, ...query, deliveryDateFrom, deliveryDateTo }));
  }, []);

  const handleFilter = (params, updatePath) => {
    delete params.pageSizeOptions;
    delete params.showSizeChanger;
    delete params.total;
    delete params.locale;
    delete params.page;
    if (updatePath) {
      navigate({
        pathname: location.pathname,
        search: queryString.stringify(params || {}, {
          skipNull: true,
        }),
      });
    }
    const newCurrentPage = Number(params?.current || 1);
    const newItemsPerPage = Number(params?.pageSize) || PAGE_SIZE;
    const deliveryDateFrom = params?.deliveryDateFrom
      ? dayjs(params?.deliveryDateFrom).startOf('day').toISOString()
      : null;
    const deliveryDateTo = params?.deliveryDateTo ? dayjs(params?.deliveryDateTo).endOf('day').toISOString() : null;
    dispatch(
      doGetJobOrders({
        page: newCurrentPage,
        take: newItemsPerPage,
        ...params,
        deliveryDateFrom,
        deliveryDateTo,
      }),
    );
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch && handleSearch();
    }
  };

  const handleTableChange = (params) => {
    const newParams = { ...query, ...params };
    handleFilter(newParams, true);
  };

  const handleChangeSearch = (e) => {
    setSearchValue(e.target.value);
  };

  const handleSearch = () => {
    handleFilter({ q: searchValue || '' }, true);
  };

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const handleExportCsv = async () => {
    const { q, status, deliveryDateFrom, deliveryDateTo, clientId, driverId, jobOrderId, driverName } = query;
    const params = {
      q,
      status,
      deliveryDateFrom,
      deliveryDateTo,
      clientId,
      driverId,
      jobOrderId,
      driverName,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
    try {
      const { response, error } = await jobOrdersApi.exportJobOrdersCsv(params);
      if (error || !response) return;
      const blob = response instanceof Blob ? response : new Blob([response], { type: 'text/csv;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `job-orders-${Date.now()}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      // errors handled globally
    }
  };
  const handleUnassign = async (record) => {
    try {
      await dispatch(doUnassignJobOrder(record.orderId)).unwrap();
      handleFilter(
        {
          ...query,
          current: currentPage,
          pageSize: itemsPerPage,
        },
        false,
      );
    } catch (e) {
      // errors handled globally
    }
  };
  const withAction = (record) => {
    return (
      <>
        <div className="actions">
          <Button type="link" className="view-details" onClick={() => navigate('/job-orders/' + record.orderId)}>
            <ReactSVG className="icon" src="/icons/view-details.svg" />
          </Button>

          {record.paymentStatus !== 'PAID' && (
            <Button type="link" className="payment" onClick={() => navigate(`/job-orders/payment/${record.orderId}`)}>
              <ReactSVG className="icon" src="/icons/payment.svg" />
            </Button>
          )}
          {record.driver && record.status === 'ACCEPTED' && (
            <Popconfirm
              title="Unassign driver from this job?"
              okText="Yes"
              cancelText="No"
              onConfirm={() => handleUnassign(record)}
            >
              <Button type="link" className="unassign">
                <ReactSVG className="icon" src="/icons/cancel.svg" />
              </Button>
            </Popconfirm>
          )}
        </div>
      </>
    );
  };
  return (
    <Spin spinning={false}>
      <div className="Content JobOrdersPage">
        <div className="ContentHeader">
          <div className="HeaderTitle">JOB ORDERS</div>
          <div className="HeaderToolBar">
            <div className="ToolBarButtons">
              <Link to="/job-orders/create">
                <Button className="CreateButton ButtonPrimary" type="primary" size="small">
                  Create New Order
                  <ReactSVG className="icon" src="/icons/add.svg" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
        <div className="orders-header-table page-header-table">
          <div className="search">
            <Input
              placeholder="Search"
              suffix={<img src="/icons/search.svg" />}
              onChange={handleChangeSearch}
              onBlur={handleSearch}
              onKeyDown={handleKeyDown}
              disabled={isFetching}
              value={searchValue}
            />
          </div>
          <div className="buttons">
            {/* <Button className="FilterButton ButtonFilter">
              Action <ReactSVG className="icon" src="/icons/arrow_drop_down.svg" />
            </Button> */}
            <Button
              className={`FilterButton ButtonFilter ${hasActiveQueryParams ? 'ButtonFilterActive' : ''}`}
              onClick={() => setIsFilterOpen(!isFilterOpen)}
            >
              Filter
              <ReactSVG className="icon" src="/icons/filter_list.svg" />
            </Button>
            <Button className="FilterButton ButtonFilter" onClick={handleExportCsv}>
              Export CSV
              <ReactSVG className="icon" src="/icons/list_alt.svg" />
            </Button>
          </div>
        </div>
        {isFilterOpen && <JobOrdersFilter isFetching={false} query={query} onSubmit={handleFilter} />}
        <div className="ContentMain">
          <JobOrdersTable
            dataSource={jobOrders}
            pagination={pagination}
            withAction={withAction}
            selectedRowKeys={selectedRowKeys}
            onSelectChange={onSelectChange}
            rowKey="orderId"
            isFetching={false}
            onChange={handleTableChange}
          />
        </div>
      </div>
    </Spin>
  );
};

export default JobOrdersPage;
